import React, { Component } from 'react';
import Typography from '@material-ui/core/Typography/Typography';
import AppBar from '@material-ui/core/AppBar/AppBar';
import Toolbar from '@material-ui/core/Toolbar/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import BackArrow from '@material-ui/icons/ArrowBackIos';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField/TextField';
import Button from '@material-ui/core/Button/Button';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import Snackbar from '@material-ui/core/Snackbar';
import DeleteIcon from '@material-ui/icons/DeleteForever';
import PropTypes from 'prop-types';

import SnackbarContentWrapper from '../../Components/SnackbarContentWrapper/SnackbarContentWrapper';
import './User.css';
import { db, getUser, redirectToRoot } from '../../utilities/firebase-config';
import styles from './User.style';

const defaultState = {
  formDirty: false,
  nameError: false,
  permissionsError: false,
  snackbar: {
    autoHideDuration: 3000,
    message: '',
    open: false,
    variant: 'success',
  },
  submitting: false,
  title: '',
  user: {
    added: 0,
    email: '',
    name: '',
    permissions: '',
    updated: 0,
  },
  userId: '',
  validPermissions: [],
};

/**
 * Save a user to the updateUser path
 * @param userId
 * @param user
 * @returns {Promise<string | string | *>}
 */
const saveChanges = async ({
  action,
  authedUserId,
  userId,
  user,
}) => {
  const newUser = {
    ...user,
    action,
    requestedBy: authedUserId,
    uid: userId,
  };

  // Save changes
  const { key: updateUserKey } = await db.ref('updateUser').push(newUser)
    .catch(() => {
      throw new Error('Failed to Save Changes');
    });

  return updateUserKey;
};

class User extends Component {
  state = defaultState;

  updateUserKey = '';

  originalUser = defaultState.user;

  authedUserId = '';

  deleteRequested = false;

  async componentDidMount() {
    const validPermissions = await this.getPermissions();

    const authedUser = await getUser();
    const { history } = this.props;

    if (!authedUser || !authedUser.profile || authedUser.profile.permissions !== 'Administrator') {
      redirectToRoot(history);
    }

    this.authedUserId = authedUser.uid;

    let userId = window.location.pathname.split('/')[2];
    if (userId === 'new') {
      userId = '';
    }

    if (userId) {
      this.setState({
        userId,
        validPermissions,
      });
    } else {
      this.setState({
        title: 'Add User',
        validPermissions,
      });
    }

    if (userId) {
      this.watchUser(userId);
    }
  }

  componentWillUnmount() {
    const { userId } = this.state;
    this.unwatchUser(userId);
    this.unwatchUpdateUser();
  }

  submit = async (event) => {
    const { snackbar } = this.state;
    if (event) {
      // The delete button does not call with an event
      event.preventDefault();
    }
    // NOTE: No email validation is performed.  Leaving this to Firebase
    const { userId } = this.state;
    const { user } = this.state;

    let nameError = null;
    let permissionsError = null;

    if (user.name.length < 2) {
      nameError = 'Please enter the users\'s full name';
    }

    if (user.name.length > 100) {
      nameError = 'Please enter a name less than 100 characters';
    }

    if (!user.permissions) {
      permissionsError = 'Please select a permission';
    }

    // Stop if there are any form errors
    if (nameError || permissionsError) {
      this.setState({
        nameError,
        permissionsError,
      });

      return false;
    }

    this.setState({
      submitting: true,
    });

    let action = userId ? 'update' : 'new';
    action = this.deleteRequested ? 'delete' : action;

    try {
      this.updateUserKey = await saveChanges({
        action,
        authedUserId: this.authedUserId,
        user: {
          ...user,
        },
        userId,
      });
      this.watchUpdateUser();
    } catch (e) {
      this.setState({
        snackbar: {
          ...snackbar,
          message: e.toString().replace('Error: ', ''),
          open: true,
          variant: 'error',
        },
        submitting: false,
      });

      this.deleteRequested = false;
      return false;
    }

    // Show snackbar, prevent it from hiding automatically
    this.setState({
      snackbar: {
        ...snackbar,
        autoHideDuration: 1000000,
        message: 'Updating user.  Please wait.',
        open: true,
        variant: 'info',
      },
    });

    return false;
  };

  changeField = (event) => {
    const { user } = this.state;

    this.setState({
      formDirty: true,
      nameError: false,
      permissionsError: false,
      user: {
        ...user,
        [event.target.name]: event.target.value,
      },
    });
  };

  closeSnackbar = async () => {
    const { snackbar } = this.state;

    return new Promise((resolve) => {
      // Don't change the color or it will confuse user
      this.setState({
        snackbar: {
          ...snackbar,
          autoHideDuration: 3000,
          message: '',
          open: false,
        },
      }, () => {
        setTimeout(() => {
          resolve(true);
        }, 500);
      });
    });
  };

  getPermissions = async () => {
    const snapshot = await db.ref('permissions').once('value');
    const values = snapshot.val();

    if (values) {
      return Object.keys(values).sort();
    }

    return [];
  }

  deleteUser = () => {
    this.deleteRequested = true;
    this.submit();
  }

  watchUser = (userId) => {
    db.ref(`users/${userId}`).on('value', (snapshot) => {
      const data = snapshot.val();
      if (!data) return;

      if (data) {
        this.setState({
          title: data.name,
          user: {
            ...data,
          },
        }, () => {
          const { user } = this.state;
          this.originalUser = user;
        });
      }
    });
  };

  watchUpdateUser = () => {
    const { history } = this.props;
    const { snackbar, userId } = this.state;

    db.ref(`updateUser/${this.updateUserKey}`).on('value', (snapshot) => {
      const data = snapshot.val();
      if (data.status) {
        this.unwatchUpdateUser();

        // The ternary makes this much easier to read and use
        // eslint-disable-next-line no-unneeded-ternary
        const formDirty = data.status === 'success' ? false : true;
        // Wait long enough for the user to notice the change
        setTimeout(async () => {
          await this.closeSnackbar();

          this.setState({
            formDirty,
            snackbar: {
              ...snackbar,
              autoHideDuration: 5000,
              message: data.message,
              open: true,
              variant: data.status,
            },
            submitting: false,
          }, () => {
            if (this.deleteRequested) {
              setTimeout(() => {
                const { history: { goBack } } = this.props;
                goBack();
              }, 1500);
            }
          });
        }, 1000);

        if (data.uid !== userId) {
          const location = {
            pathname: `${data.uid}`,
          };

          // Redirect to the new location
          history.replace(location);
          this.setState({
            title: data.name,
            userId: data.uid,
          });
        }
      }
    });
  }

  unwatchUser = (userId) => {
    db.ref(`users/${userId}`).off();
  };

  unwatchUpdateUser = () => {
    db.ref(`updateUser/${this.updateUserKey}`).off();
  };

  goBack = () => {
    const { history } = this.props;
    history.goBack();
  };

  reset = () => {
    this.setState({
      formDirty: false,
      nameError: false,
      permissionsError: false,
      user: this.originalUser,
    });
  }

  render() {
    const { classes } = this.props;
    const {
      formDirty,
      nameError,
      permissionsError,
      snackbar,
      submitting,
      title,
      user,
      userId,
      validPermissions,
    } = this.state;

    return (
      <div className="User container">

        <AppBar position="fixed" color="primary">
          <Toolbar>

            <IconButton className={ classes.menuButton } color="inherit" aria-label="Go Back" onClick={ this.goBack } disabled={ submitting }>
              <BackArrow />
            </IconButton>

            <Typography variant="h6" color="inherit" className={ classes.grow }>
              { title }
            </Typography>

            { userId && (
              <IconButton aria-label="Delete User" className={ classes.deleteButton } onClick={ this.deleteUser } disabled={ submitting }>
                <DeleteIcon />
              </IconButton>
            )}

          </Toolbar>
        </AppBar>

        <Snackbar
          anchorOrigin={ {
            horizontal: 'left',
            vertical: 'bottom',
          } }
          open={ snackbar.open }
          autoHideDuration={ snackbar.autoHideDuration }
          onClose={ this.closeSnackbar }
        >
          <SnackbarContentWrapper
            onClose={ this.closeSnackbar }
            variant={ snackbar.variant }
            message={ snackbar.message }
          />
        </Snackbar>

        { userId && (
          <Typography gutterBottom variant="h5" component="h2">
            Edit User
          </Typography>
        )
        }

        <form onSubmit={ this.submit } name="edit-user">

          <TextField
            className={ classes.textField }
            id="name"
            label="Full Name"
            margin="normal"
            name="name"
            onChange={ this.changeField }
            value={ user.name }
            type="text"
            disabled={ submitting }
          />

          { nameError
          && <div className={ classes.error }>{ nameError }</div>
          }

          <TextField
            id="email"
            name="email"
            onChange={ this.changeField }
            value={ user.email }
            label="Email Address"
            className={ classes.textField }
            margin="normal"
            type="email"
            disabled={ submitting }
          />

          <div>
            <FormControl component="fieldset" className={ classes.permissionsSection }>
              <FormLabel component="legend">Permissions</FormLabel>
              <RadioGroup
                aria-label="Permissions"
                name="permissions"
                value={ user.permissions }
                className={ classes.radioGroup }
                onChange={ this.changeField }
              >
                {
                  validPermissions.map(permission => (
                    <FormControlLabel
                      key={ permission }
                      value={ permission }
                      control={ <Radio color="primary" disabled={ submitting } /> }
                      label={ permission }
                    />
                  ))
                }
              </RadioGroup>

              { permissionsError
                && <div className={ classes.error }>{ permissionsError }</div>
              }

            </FormControl>
          </div>

          <Button
            variant="outlined"
            color="primary"
            className={ classes.button }
            onClick={ this.submit }
            type="submit"
            disabled={ submitting || !formDirty }
          >
            Save
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            className={ classes.button }
            onClick={ this.reset }
            disabled={ submitting || !formDirty }
          >
            Reset
          </Button>
        </form>

      </div>
    );
  }
}

User.propTypes = {
  classes: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default withStyles(styles)(User);
